import React, { useCallback, useMemo, useState } from 'react';
import {
  IconCalendarEvent,
  IconCategory,
  IconCircleDot,
  IconCreditCard,
  IconTags,
} from '@tabler/icons-react';
import { getStatusMeta, STATUS_META } from '../utils/reembolsosStatus';
import {
  expenseMatchesSliceFilters,
  formatBrl,
  formatPaymentType,
  rebuildTechCardFromExpenses,
  techMatchesExpense,
} from '../utils/reembolsosFormat';
import {
  BoardToolbarAreaClickUpChip,
  BoardToolbarCoordenadorClickUpChip,
  BoardToolbarTecnicosClickUpChip,
} from '../../board/toolbar';
import { BoardClickUpFilterChipDropdown } from '../../board/toolbar/BoardClickUpFilterChipDropdown';
import { BoardToolbarRefreshClickUpButton } from '../../board/toolbar/BoardToolbarRefreshClickUpButton';
import GestaoExpenseTypesModal from './GestaoExpenseTypesModal';
import GestaoReembolsosNewExpenseModal from './GestaoReembolsosNewExpenseModal';
import { GrHoverTooltip } from './GestaoReembolsosTooltip';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import './GestaoReembolsosNewExpenseModal.css';

/** Filtro overview: situação do período no card do técnico */
const PERIOD_SITUATION_OPTIONS = [
  'Período vigente',
  'Período anterior com pendência',
  'Histórico',
];
const PERIOD_SIT_VIGENTE = PERIOD_SITUATION_OPTIONS[0];
const PERIOD_SIT_PAST_OPEN = PERIOD_SITUATION_OPTIONS[1];
const PERIOD_SIT_HISTORY = PERIOD_SITUATION_OPTIONS[2];

function techMatchesPeriodSituation(tech, selected) {
  if (!selected?.length) return true;
  const wantVigente = selected.includes(PERIOD_SIT_VIGENTE);
  const wantPast = selected.includes(PERIOD_SIT_PAST_OPEN);
  const wantHist = selected.includes(PERIOD_SIT_HISTORY);
  if (wantVigente && Boolean(tech.periodIsActive)) return true;
  if (wantPast && Boolean(tech.showPastOpenHint)) return true;
  if (wantHist && Boolean(tech.isHistory)) return true;
  return false;
}

/** Histórico só entra na lista se o usuário filtrar coord/área/técnico (ou Situação=Histórico). */
function historyAllowedByHierarchy(tech, coordName, areaName, { coordsSel, areasSel, techsSel, periodSitSel }) {
  if (!tech?.isHistory) return true;
  if (periodSitSel?.includes(PERIOD_SIT_HISTORY)) return true;
  if (techsSel?.length && techsSel.includes(tech.name)) return true;
  if (areasSel?.length && areasSel.includes(areaName)) return true;
  if (coordsSel?.length && coordsSel.includes(coordName)) return true;
  return false;
}

function pastOpenTooltipContent(periods) {
  const list = Array.isArray(periods) ? periods.filter((p) => p?.label) : [];
  if (!list.length) {
    return 'Há período encerrado com pendência — abra o detalhe do técnico.';
  }
  if (list.length === 1) {
    return (
      <>
        <span className="gr-tip-kicker">Período com pendência</span>
        <span className="gr-tip-line">{list[0].label}</span>
      </>
    );
  }
  return (
    <>
      <span className="gr-tip-kicker">
        {list.length} períodos com pendência
      </span>
      <ul className="gr-tip-list">
        {list.map((p) => (
          <li key={`${p.periodStart}|${p.periodEnd}`}>{p.label}</li>
        ))}
      </ul>
    </>
  );
}

function Avatar({ name, initials, color, photoUrl, size = 34 }) {
  const [imgErr, setImgErr] = useState(false);
  const showImg = Boolean(photoUrl) && !imgErr;
  return (
    <span
      className="gr-avatar"
      style={{
        width: size,
        height: size,
        background: showImg ? 'transparent' : color || '#7c4dff',
      }}
      aria-hidden
    >
      {showImg ? (
        <img
          src={photoUrl}
          alt=""
          className="gr-avatar-img"
          onError={() => setImgErr(true)}
        />
      ) : (
        <span className="gr-avatar-initials">{initials || '?'}</span>
      )}
    </span>
  );
}

function TechCard({ tech, selected, onOpen }) {
  const periodLabel = tech.periodLabel || tech.lastLabel || '—';
  const isActive = Boolean(tech.periodIsActive);
  const isHistory = Boolean(tech.isHistory);
  const showPastHint = Boolean(tech.showPastOpenHint);
  const pastOpenCount = Number(tech.pastOpenCount) || 0;
  const pastOpenPeriods = tech.pastOpenPeriods || [];
  /** Só período(s) anterior(es) com pendência — sem período vigente: só Pendente/Revisado */
  const onlyOpenStatuses = showPastHint && !isActive && !isHistory;

  const visibleStats = (tech.stats || []).filter((st) => {
    if (!(Number(st.amount) > 0)) return false;
    if (onlyOpenStatuses) {
      return st.statusKey === 'pendente' || st.statusKey === 'revisado';
    }
    return true;
  });

  const visibleSegments = (tech.segments || []).filter((seg) => {
    if (!(Number(seg.widthPct) > 0)) return false;
    if (onlyOpenStatuses) {
      return seg.statusKey === 'pendente' || seg.statusKey === 'revisado';
    }
    return true;
  });

  const barSumPct =
    visibleSegments.reduce((s, x) => s + (Number(x.widthPct) || 0), 0) || 1;

  return (
    <button
      type="button"
      className={[
        'gr-tech-card',
        selected ? 'is-selected' : '',
        showPastHint && !isActive ? 'has-past-open' : '',
        isActive ? 'is-period-active has-period-hint' : '',
        isHistory ? 'is-history has-period-hint' : '',
        onlyOpenStatuses ? 'is-past-open-only' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => onOpen(tech)}
    >
      <div className="gr-tech-card-head">
        <Avatar
          name={tech.name}
          initials={tech.initials}
          color={tech.avatarColor}
          photoUrl={tech.profilePhotoUrl}
          size={34}
        />
        <div className="gr-tech-card-id">
          <span className="gr-tech-card-name">{tech.name}</span>
          {isHistory ? (
            <GrHoverTooltip
              content="Período encerrado sem pendência — consulta histórica"
              delayMs={180}
            >
              <span className="gr-tech-card-period-hint is-history">
                <span className="gr-tech-card-period-hint-text">Histórico</span>
              </span>
            </GrHoverTooltip>
          ) : isActive ? (
            <GrHoverTooltip content="O período atual ainda está em andamento" delayMs={180}>
              <span className="gr-tech-card-period-hint is-active">
                <span className="gr-tech-card-period-hint-text">Período vigente</span>
              </span>
            </GrHoverTooltip>
          ) : showPastHint ? (
            <GrHoverTooltip
              content={pastOpenTooltipContent(pastOpenPeriods)}
              multiline
              delayMs={160}
            >
              <span className="gr-tech-card-period-hint is-past">
                <span className="gr-tech-card-period-hint-text">
                  Período anterior com pendência
                </span>
                {pastOpenCount > 1 ? (
                  <span
                    className="gr-tech-card-period-count-badge"
                    aria-label={`${pastOpenCount} períodos com pendência`}
                  >
                    {pastOpenCount}
                  </span>
                ) : null}
              </span>
            </GrHoverTooltip>
          ) : null}
        </div>
      </div>

      <div className="gr-tech-card-total">
        <span className="gr-tech-card-currency">R$</span>
        <span className="gr-tech-card-amount">{tech.totalLabel}</span>
      </div>

      <div className="gr-tech-card-meta">
        <span>{tech.countLabel}</span>
        {tech.pendAlert ? <span className="gr-tech-card-pend">{tech.pendAlert}</span> : null}
      </div>

      <div className="gr-tech-card-bar" aria-hidden>
        {visibleSegments.map((seg) => {
          const meta = getStatusMeta(seg.statusKey);
          const w = ((Number(seg.widthPct) || 0) / barSumPct) * 100;
          return (
            <span
              key={seg.statusKey}
              style={{ width: `${w}%`, background: meta.dot }}
            />
          );
        })}
      </div>

      <div className="gr-tech-card-stats">
        {visibleStats.map((st) => {
          const meta = getStatusMeta(st.statusKey);
          return (
            <div
              key={st.statusKey}
              className="gr-tech-stat"
              style={{
                background: meta.bg,
                borderColor: meta.bd,
              }}
            >
              <span className="gr-tech-stat-dot" style={{ background: meta.dot }} />
              <span className="gr-tech-stat-label">{meta.label}</span>
              <span className="gr-tech-stat-value">{st.value}</span>
            </div>
          );
        })}
      </div>

      <div className="gr-tech-card-foot">
        {(isActive || isHistory) && periodLabel && periodLabel !== '—' ? (
          <span className="gr-tech-card-last">{periodLabel}</span>
        ) : (
          <span className="gr-tech-card-last" aria-hidden="true" />
        )}
        <span className="gr-tech-card-go">Ver →</span>
      </div>
    </button>
  );
}

function toggleSelection(list, value) {
  if (list.includes(value)) return list.filter((x) => x !== value);
  return [...list, value];
}

/**
 * Overview: coordenadores → áreas → técnicos (dados LCMY_GO_EXPENSES).
 */
export default function GestaoReembolsosOverview({
  coordinators = [],
  expenses = [],
  selectedCodtech,
  onOpenTech,
  onRefresh,
  refreshing = false,
  canCreateExpense = false,
}) {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [openFilter, setOpenFilter] = useState(null);
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  const [newExpenseOpen, setNewExpenseOpen] = useState(false);
  const [coordsSel, setCoordsSel] = useState([]);
  const [areasSel, setAreasSel] = useState([]);
  const [techsSel, setTechsSel] = useState([]);
  const [catsSel, setCatsSel] = useState([]);
  const [statusSel, setStatusSel] = useState([]);
  const [paySel, setPaySel] = useState([]);
  const [periodSitSel, setPeriodSitSel] = useState([]);

  const hierarchyMeta = useMemo(() => {
    const coordHistOnly = new Map(); // name -> boolean (all techs history)
    const areaHistOnly = new Map();
    const techHist = new Map(); // name -> boolean
    for (const c of coordinators) {
      let coordHasAction = false;
      let coordHasAny = false;
      for (const a of c.areas || []) {
        let areaHasAction = false;
        let areaHasAny = false;
        for (const t of a.technicians || []) {
          if (!t.name) continue;
          const hist = Boolean(t.isHistory);
          techHist.set(t.name, techHist.has(t.name) ? techHist.get(t.name) && hist : hist);
          areaHasAny = true;
          coordHasAny = true;
          if (!hist) {
            areaHasAction = true;
            coordHasAction = true;
          }
        }
        if (a.name && areaHasAny) {
          const onlyHist = !areaHasAction;
          areaHistOnly.set(
            a.name,
            areaHistOnly.has(a.name) ? areaHistOnly.get(a.name) && onlyHist : onlyHist
          );
        }
      }
      if (c.name && coordHasAny) {
        const onlyHist = !coordHasAction;
        coordHistOnly.set(
          c.name,
          coordHistOnly.has(c.name) ? coordHistOnly.get(c.name) && onlyHist : onlyHist
        );
      }
    }
    return { coordHistOnly, areaHistOnly, techHist };
  }, [coordinators]);

  const coordOptions = useMemo(() => {
    const names = coordinators.map((c) => c.name).filter(Boolean);
    return [...new Set(names)].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [coordinators]);

  const areaOptions = useMemo(() => {
    const names = new Set();
    for (const c of coordinators) {
      for (const a of c.areas || []) {
        if (a.name) names.add(a.name);
      }
    }
    return [...names].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [coordinators]);

  const techOptions = useMemo(() => {
    const names = new Set();
    for (const c of coordinators) {
      for (const a of c.areas || []) {
        for (const t of a.technicians || []) {
          if (t.name) names.add(t.name);
        }
      }
    }
    return [...names].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [coordinators]);

  const histSuffix = useCallback(
    (kind) => (name) => {
      if (kind === 'coord' && hierarchyMeta.coordHistOnly.get(name)) return 'Histórico';
      if (kind === 'area' && hierarchyMeta.areaHistOnly.get(name)) return 'Histórico';
      if (kind === 'tech' && hierarchyMeta.techHist.get(name)) return 'Histórico';
      return '';
    },
    [hierarchyMeta]
  );

  const categoryOptions = useMemo(() => {
    const names = new Set();
    for (const e of expenses) {
      const n = String(e.expenseTypeName || '').trim();
      if (n) names.add(n);
    }
    return [...names].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [expenses]);

  const statusOptions = useMemo(
    () => Object.keys(STATUS_META).map((key) => STATUS_META[key].label),
    []
  );

  const paymentOptions = useMemo(() => {
    const names = new Set();
    for (const e of expenses) {
      const n = formatPaymentType(e.paymentType);
      if (n) names.add(n);
    }
    return [...names].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [expenses]);

  const statusLabelToKey = useMemo(() => {
    const map = {};
    for (const [key, meta] of Object.entries(STATUS_META)) {
      map[meta.label] = key;
    }
    return map;
  }, []);

  const sliceFilter = useMemo(() => {
    const statusKeys = statusSel
      .map((label) => statusLabelToKey[label])
      .filter(Boolean);
    return {
      categories: catsSel,
      statusKeys,
      paymentLabels: paySel,
      active: Boolean(catsSel.length || statusKeys.length || paySel.length),
    };
  }, [catsSel, statusSel, paySel, statusLabelToKey]);

  const filtered = useMemo(() => {
    const hierCtx = { coordsSel, areasSel, techsSel, periodSitSel };
    return coordinators
      .filter((c) => {
        if (!coordsSel.length) return true;
        return coordsSel.includes(c.name);
      })
      .map((c) => {
        let coordPend = 0;
        let coordTotal = 0;
        const areas = (c.areas || [])
          .filter((a) => {
            if (!areasSel.length) return true;
            return areasSel.includes(a.name);
          })
          .map((a) => {
            const techs = (a.technicians || [])
              .filter((t) => {
                if (techsSel.length && !techsSel.includes(t.name)) return false;
                if (!techMatchesPeriodSituation(t, periodSitSel)) return false;
                if (!historyAllowedByHierarchy(t, c.name, a.name, hierCtx)) return false;
                return true;
              })
              .map((t) => {
                const techsExps = expenses.filter((e) => {
                  if (!techMatchesExpense(t, e)) return false;
                  if (sliceFilter.active && !expenseMatchesSliceFilters(e, sliceFilter)) {
                    return false;
                  }
                  return true;
                });
                if (!techsExps.length) return null;
                return rebuildTechCardFromExpenses(t, techsExps);
              })
              .filter(Boolean);

            if (!techs.length) return null;

            const areaTotal = techs.reduce((s, t) => s + (Number(t.total) || 0), 0);
            coordPend += techs.reduce((s, t) => {
              const pendStat = (t.stats || []).find((st) => st.statusKey === 'pendente');
              return s + (Number(pendStat?.amount) || 0);
            }, 0);
            coordTotal += areaTotal;

            return {
              ...a,
              technicians: techs,
              techCount: techs.length,
              meta: `${techs.length} ${techs.length === 1 ? 'técnico' : 'técnicos'}`,
              total: areaTotal,
              totalLabel: formatBrl(areaTotal),
            };
          })
          .filter(Boolean);

        if (!areas.length) return null;

        return {
          ...c,
          areas,
          pendTotalLabel: `R$ ${formatBrl(coordPend)}`,
          periodTotalLabel: `R$ ${formatBrl(coordTotal)}`,
        };
      })
      .filter(Boolean);
  }, [coordinators, coordsSel, areasSel, techsSel, periodSitSel, expenses, sliceFilter]);

  const toggleCoord = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openChange = useCallback((key) => (open) => {
    setOpenFilter(open ? key : null);
  }, []);

  return (
    <div className="gr-overview">
      <div className="filters-bar gr-filters-bar">
        <div className="filter-buttons">
          <div className="filter-group board-toolbar-filter-group">
            <BoardToolbarCoordenadorClickUpChip
              options={coordOptions}
              selected={coordsSel}
              getOptionSuffix={histSuffix('coord')}
              isOpen={openFilter === 'coordenador'}
              onOpenChange={openChange('coordenador')}
              onToggleItem={(v) => setCoordsSel((prev) => toggleSelection(prev, v))}
              onToggleAll={(keys, allMarked) => {
                setCoordsSel((prev) =>
                  allMarked
                    ? prev.filter((x) => !keys.includes(x))
                    : Array.from(new Set([...prev, ...keys]))
                );
              }}
              onClear={() => setCoordsSel([])}
              onApply={() => setOpenFilter(null)}
            />
            <BoardToolbarAreaClickUpChip
              options={areaOptions}
              selected={areasSel}
              getOptionSuffix={histSuffix('area')}
              isOpen={openFilter === 'area'}
              onOpenChange={openChange('area')}
              onToggleItem={(v) => setAreasSel((prev) => toggleSelection(prev, v))}
              onToggleAll={(keys, allMarked) => {
                setAreasSel((prev) =>
                  allMarked
                    ? prev.filter((x) => !keys.includes(x))
                    : Array.from(new Set([...prev, ...keys]))
                );
              }}
              onClear={() => setAreasSel([])}
              onApply={() => setOpenFilter(null)}
            />
            <BoardToolbarTecnicosClickUpChip
              options={techOptions}
              selected={techsSel}
              getOptionSuffix={histSuffix('tech')}
              isOpen={openFilter === 'tecnico'}
              onOpenChange={openChange('tecnico')}
              onToggleItem={(v) => setTechsSel((prev) => toggleSelection(prev, v))}
              onToggleAll={(keys, allMarked) => {
                setTechsSel((prev) =>
                  allMarked
                    ? prev.filter((x) => !keys.includes(x))
                    : Array.from(new Set([...prev, ...keys]))
                );
              }}
              onClear={() => setTechsSel([])}
              onApply={() => setOpenFilter(null)}
            />
            <span className="board-toolbar-separator" aria-hidden="true">
              |
            </span>
            <BoardClickUpFilterChipDropdown
              label="Categoria"
              icon={IconCategory}
              options={categoryOptions}
              selected={catsSel}
              isOpen={openFilter === 'categoria'}
              onOpenChange={openChange('categoria')}
              onToggleItem={(v) => setCatsSel((prev) => toggleSelection(prev, v))}
              onToggleAll={(keys, allMarked) => {
                setCatsSel((prev) =>
                  allMarked
                    ? prev.filter((x) => !keys.includes(x))
                    : Array.from(new Set([...prev, ...keys]))
                );
              }}
              onClear={() => setCatsSel([])}
              onApply={() => setOpenFilter(null)}
              pickerMode="simple"
              pickerWidth={238}
              pickerMaxHeight={320}
              searchPlaceholder="Buscar"
              countLabel="categorias"
              listAriaLabel="Categorias de despesa"
              popoverClassName="board-search-filters-popover--categoria gr-module-popover"
              emptyMessage="Nenhuma categoria encontrada"
            />
            <BoardClickUpFilterChipDropdown
              label="Status"
              icon={IconCircleDot}
              options={statusOptions}
              selected={statusSel}
              isOpen={openFilter === 'status'}
              onOpenChange={openChange('status')}
              onToggleItem={(v) => setStatusSel((prev) => toggleSelection(prev, v))}
              onToggleAll={(keys, allMarked) => {
                setStatusSel((prev) =>
                  allMarked
                    ? prev.filter((x) => !keys.includes(x))
                    : Array.from(new Set([...prev, ...keys]))
                );
              }}
              onClear={() => setStatusSel([])}
              onApply={() => setOpenFilter(null)}
              pickerMode="simple"
              pickerWidth={238}
              pickerMaxHeight={320}
              searchPlaceholder="Buscar"
              countLabel="status"
              listAriaLabel="Status da despesa"
              popoverClassName="board-search-filters-popover--status gr-module-popover"
              emptyMessage="Nenhum status encontrado"
              showSearch={false}
            />
            <BoardClickUpFilterChipDropdown
              label="Forma de pagamento"
              icon={IconCreditCard}
              options={paymentOptions}
              selected={paySel}
              isOpen={openFilter === 'pagamento'}
              onOpenChange={openChange('pagamento')}
              onToggleItem={(v) => setPaySel((prev) => toggleSelection(prev, v))}
              onToggleAll={(keys, allMarked) => {
                setPaySel((prev) =>
                  allMarked
                    ? prev.filter((x) => !keys.includes(x))
                    : Array.from(new Set([...prev, ...keys]))
                );
              }}
              onClear={() => setPaySel([])}
              onApply={() => setOpenFilter(null)}
              pickerMode="simple"
              pickerWidth={260}
              pickerMaxHeight={320}
              searchPlaceholder="Buscar"
              countLabel="formas"
              listAriaLabel="Formas de pagamento"
              popoverClassName="board-search-filters-popover--pagamento gr-module-popover"
              emptyMessage="Nenhuma forma encontrada"
            />
            <BoardClickUpFilterChipDropdown
              label="Situação do período"
              icon={IconCalendarEvent}
              options={PERIOD_SITUATION_OPTIONS}
              selected={periodSitSel}
              isOpen={openFilter === 'periodSit'}
              onOpenChange={openChange('periodSit')}
              onToggleItem={(v) => setPeriodSitSel((prev) => toggleSelection(prev, v))}
              onToggleAll={(keys, allMarked) => {
                setPeriodSitSel((prev) =>
                  allMarked
                    ? prev.filter((x) => !keys.includes(x))
                    : Array.from(new Set([...prev, ...keys]))
                );
              }}
              onClear={() => setPeriodSitSel([])}
              onApply={() => setOpenFilter(null)}
              pickerMode="simple"
              pickerWidth={280}
              pickerMaxHeight={280}
              searchPlaceholder="Buscar"
              countLabel="situações"
              listAriaLabel="Situação do período"
              popoverClassName="board-search-filters-popover--period-sit gr-module-popover"
              emptyMessage="Nenhuma situação encontrada"
              showSearch={false}
            />
          </div>

          <div className="search-container board-toolbar-right">
            <BoardToolbarRefreshClickUpButton
              onClick={() => onRefresh?.()}
              disabled={Boolean(refreshing) || !onRefresh}
              isRefreshing={Boolean(refreshing)}
            />
            <span className="board-toolbar-separator" aria-hidden="true">
              |
            </span>
            <div className="board-toolbar-actions-group gr-filters-actions">
              {canCreateExpense ? (
                <button
                  type="button"
                  className="gr-new-expense-btn"
                  aria-label="Nova despesa"
                  onClick={() => setNewExpenseOpen(true)}
                >
                  <span className="gr-new-expense-btn-plus" aria-hidden="true">
                    +
                  </span>
                  Nova despesa
                </button>
              ) : null}
              <div className="board-clickup-filter-chip-anchor">
                <button
                  type="button"
                  className="board-clickup-filter-chip"
                  aria-label="Gestão de categorias"
                  onClick={() => setCategoriesModalOpen(true)}
                >
                  <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                    <IconTags size={13} stroke={1.5} />
                  </span>
                  <span className="board-clickup-filter-chip-text">
                    <span className="board-clickup-filter-chip-label">Gestão de categorias</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <GestaoReembolsosNewExpenseModal
        open={newExpenseOpen}
        onClose={() => setNewExpenseOpen(false)}
        preferredCodtech={selectedCodtech}
        onCreated={() => {
          setNewExpenseOpen(false);
          onRefresh?.();
        }}
      />
      <GestaoExpenseTypesModal
        open={categoriesModalOpen}
        onClose={() => setCategoriesModalOpen(false)}
      />

      {!filtered.length ? (
        <div className="gr-empty">Nenhum lançamento encontrado.</div>
      ) : (
        <div className="gr-coord-list">
          {filtered.map((coord) => {
            const cKey = String(coord.id ?? coord.name);
            const isCollapsed = collapsed.has(cKey);
            const techCount = (coord.areas || []).reduce(
              (n, a) => n + (a.technicians?.length || 0),
              0
            );
            const areaCount = (coord.areas || []).length || 1;
            const cardW = 256;
            const gap = 10;
            const areasWidth = (coord.areas || []).reduce((sum, a) => {
              const n = Math.max(a.technicians?.length || 0, 1);
              return sum + n * cardW + Math.max(n - 1, 0) * gap + 4;
            }, 0);
            const contentPad = 28;
            const basisPx = isCollapsed
              ? 320
              : Math.max(288, areasWidth + contentPad + Math.max(areaCount - 1, 0) * 12);
            const densityClass =
              techCount <= 1 ? ' is-slim' : techCount <= 3 ? ' is-compact' : ' is-wide';
            return (
              <section
                key={cKey}
                className={`gr-coord-block${densityClass}${isCollapsed ? ' is-collapsed' : ''}`}
                style={{
                  flex: `0 1 ${basisPx}px`,
                  maxWidth: isCollapsed ? 420 : '100%',
                }}
              >
                <GrHoverTooltip
                  className="gr-coord-head-tip"
                  content={isCollapsed ? 'Expandir equipe' : 'Recolher equipe'}
                  delayMs={200}
                >
                  <button
                    type="button"
                    className="gr-coord-head"
                    onClick={() => toggleCoord(cKey)}
                    aria-expanded={!isCollapsed}
                  >
                    <Avatar
                      name={coord.name}
                      initials={coord.initials}
                      color={coord.color}
                      photoUrl={coord.profilePhotoUrl}
                      size={34}
                    />
                    <div className="gr-coord-title">
                      <span className="gr-coord-name">{coord.name}</span>
                      <div className="gr-coord-summary">
                        <div>
                          <span className="gr-coord-sum-label">Pendentes</span>
                          <span className="gr-coord-sum-value is-pend">{coord.pendTotalLabel}</span>
                        </div>
                        <div>
                          <span className="gr-coord-sum-label">Total período</span>
                          <span className="gr-coord-sum-value">{coord.periodTotalLabel}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                </GrHoverTooltip>

                {!isCollapsed ? (
                  <div className="gr-area-row">
                    {coord.areas.map((area) => {
                      const nTechs = Math.max(area.technicians?.length || 0, 1);
                      const areaBasis = nTechs * cardW + Math.max(nTechs - 1, 0) * gap;
                      return (
                        <div
                          key={String(area.id ?? area.name)}
                          className="gr-area-block"
                          style={{
                            flex: `0 1 ${areaBasis}px`,
                            minWidth: Math.min(areaBasis, cardW),
                          }}
                        >
                          <div className="gr-area-head">
                            <span className="gr-area-dot" style={{ background: coord.color }} />
                            <span className="gr-area-name">{area.name}</span>
                            <span className="gr-area-meta">{area.meta}</span>
                            <span className="gr-area-total">R$ {area.totalLabel}</span>
                          </div>
                          <div className="gr-tech-grid">
                            {area.technicians.map((tech) => (
                              <TechCard
                                key={String(tech.codtech || tech.name)}
                                tech={tech}
                                selected={
                                  selectedCodtech != null &&
                                  String(selectedCodtech) === String(tech.codtech)
                                }
                                onOpen={onOpenTech}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
